import React, { useState, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  IconButton,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTheme, alpha } from "@mui/material/styles";
import { useTenant } from "../contexts/TenantContext";
import { submitCourseForApproval, fetchClassTemplates } from "../api/backend";
import { useConfigList } from "../hooks/useConfigList";

import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckIcon from "@mui/icons-material/Check";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import SendIcon from "@mui/icons-material/Send";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import LinkIcon from "@mui/icons-material/Link";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SchoolIcon from "@mui/icons-material/School";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import InfoIcon from "@mui/icons-material/Info";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

// ── Design tokens ─────────────────────────────────────────────────────────────
const DS = {
  ink: "var(--color-ink)",
  ink2: "var(--color-ink-2)",
  ink3: "var(--color-ink-3)",
  ink4: "var(--color-ink-4)",
  line: "var(--color-hairline-cloud)",
  line2: "var(--color-surface-press-light)",
  line3: "var(--color-surface-2)",
  surface: "#FFFFFF",
  surface2: "var(--color-surface-press-light)",
  surface3: "var(--color-surface-press-light)",
  pageBg: "var(--color-surface-2)",
  danger: "#B42318",
  danger50: "rgba(180,35,24,.08)",
  danger100: "rgba(180,35,24,.18)",
  shadowSm: "0 1px 0 rgba(20,24,40,.04), 0 1px 3px rgba(20,24,40,.06)",
  shadowMd: "0 8px 24px -10px rgba(20,24,40,.15)",
};

const DEFAULT_CATEGORIES = [
  "Firefighter",
  "EMS",
  "Hazardous Materials",
  "Fire Officer",
  "Technical Rescue",
  "Other",
];

const STEPS = [
  { title: "Course Details", sub: "Title, level, objectives" },
  { title: "Requirements & Logistics", sub: "Prerequisites, capacity" },
  { title: "Review & Submit", sub: "Confirm and send" },
];

const LEVELS = [
  { value: "Intro", label: "Intro" },
  { value: "Inter", label: "Intermediate" },
  { value: "Adv", label: "Advanced" },
];

const TIPS = [
  {
    Icon: LightbulbOutlinedIcon,
    text: "Courses with ",
    strong: "5+ clear objectives",
    rest: " are approved 40% faster.",
  },
  {
    Icon: DescriptionOutlinedIcon,
    text: "Use ",
    strong: "industry-standard terminology",
    rest: " in descriptions for accreditation review.",
  },
  {
    Icon: AccessTimeOutlinedIcon,
    text: "Total hours must include ",
    strong: "laboratory and testing sessions",
    rest: ".",
  },
];

// ── Progress sidebar item ─────────────────────────────────────────────────────
function ProgressItem({ label, done }) {
  const theme = useTheme();
  const tSecondary = theme.palette.secondary.main;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "11px" }}>
      <Box
        sx={{
          width: 20,
          height: 20,
          borderRadius: "50%",
          flexShrink: 0,
          display: "grid",
          placeItems: "center",
          border: `2px solid ${done ? tSecondary : DS.line}`,
          bgcolor: done ? tSecondary : "#fff",
          transition: "all .2s",
        }}
      >
        {done && (
          <CheckIcon sx={{ fontSize: 10, color: "#fff", strokeWidth: 3 }} />
        )}
      </Box>
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: done ? 600 : 500,
          color: done ? DS.ink : DS.ink4,
          transition: "color .2s",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

// ── Review card ───────────────────────────────────────────────────────────────
function ReviewCard({ title, icon, children, onEdit }) {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const tPrimary10 = alpha(tPrimary, 0.06);
  return (
    <Paper
      elevation={0}
      sx={{
        bgcolor: "#fff",
        border: `1px solid ${DS.line2}`,
        borderRadius: "14px",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: "12px 18px",
          borderBottom: `1px solid ${DS.line2}`,
          bgcolor: "#fff",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {icon}
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: "0.02em",
              color: DS.ink,
              fontFamily: "var(--font-display)",
            }}
          >
            {title}
          </Typography>
        </Box>
        <Button
          onClick={onEdit}
          startIcon={<EditIcon sx={{ fontSize: 12 }} />}
          sx={{
            fontSize: 12,
            fontWeight: 700,
            color: tPrimary,
            textTransform: "none",
            p: "4px 8px",
            borderRadius: "7px",
            "&:hover": { bgcolor: tPrimary10 },
          }}
        >
          Edit
        </Button>
      </Box>
      {children}
    </Paper>
  );
}

// ── Review cell ───────────────────────────────────────────────────────────────
function ReviewCell({ label, value, full = false }) {
  return (
    <Box
      sx={{
        p: "13px 18px",
        borderBottom: full ? `1px solid ${DS.line2}` : `1px solid ${DS.line2}`,
        borderRight: full ? 0 : `1px solid ${DS.line2}`,
        gridColumn: full ? "1 / -1" : undefined,
        "&:nth-of-type(even)": { borderRight: 0 },
      }}
    >
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.07em",
          textTransform: "uppercase",
          color: DS.ink3,
          mb: "4px",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 600,
          color: DS.ink,
          lineHeight: 1.5,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function makeCatColors(tPrimary) {
  return {
    Firefighter:          { color: "#C2410C", bg: "rgba(194,65,12,.10)" },
    EMS:                  { color: "var(--color-primary)", bg: "rgba(37,99,235,.10)" },
    "Hazardous Materials":{ color: "#B5651D", bg: "rgba(181,101,29,.10)" },
    "Fire Officer":       { color: tPrimary,  bg: alpha(tPrimary, 0.08) },
    "Technical Rescue":   { color: "#15803D", bg: "rgba(21,128,61,.10)" },
  };
}
function catColor(cat, tPrimary) { return makeCatColors(tPrimary)[cat] || { color: tPrimary, bg: alpha(tPrimary, 0.08) }; }

function PrereqPickerDialog({ open, onClose, selected, onApply, tenantId }) {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const tPrimary700 = theme.palette.primary.dark;
  const tPrimary10 = alpha(tPrimary, 0.06);

  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState([]);

  React.useEffect(() => {
    if (!open) return;
    setDraft(selected);
    setSearch("");
    setCatFilter("");
    setLoading(true);
    fetchClassTemplates(tenantId, "")
      .then(d => setClasses(Array.isArray(d) ? d : []))
      .catch(() => setClasses([]))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tenantId]);

  const cats = ["All", ...Array.from(new Set(classes.map(c => c.category).filter(Boolean)))];
  const filtered = classes.filter(c => {
    const matchCat = !catFilter || catFilter === "All" || c.category === catFilter;
    const matchSearch = !search ||
      c.title?.toLowerCase().includes(search.toLowerCase()) ||
      c.courseNumber?.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const toggle = (cls) => {
    setDraft(prev =>
      prev.some(p => p.id === cls.id)
        ? prev.filter(p => p.id !== cls.id)
        : [...prev, { id: cls.id, title: cls.title, category: cls.category, courseNumber: cls.courseNumber }]
    );
  };
  const isSelected = (cls) => draft.some(p => p.id === cls.id);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: "20px", maxHeight: "82vh", display: "flex", flexDirection: "column" } }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between",
        p: "20px 24px 16px", borderBottom: `1px solid ${DS.line}`, flexShrink: 0 }}>
        <Box>
          <Box sx={{ fontSize: 17, fontWeight: 800, color: DS.ink, letterSpacing: "-.02em", fontFamily: "var(--font-display)" }}>
            Select Prerequisites
          </Box>
          <Box sx={{ fontSize: "12.5px", color: DS.ink3, mt: "2px" }}>
            Students must have a completion certificate for each selected course
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: DS.ink3, "&:hover": { bgcolor: DS.surface2 } }}>
          <Box component="svg" width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </Box>
        </IconButton>
      </Box>

      {/* Search */}
      <Box sx={{ px: "20px", pt: "14px", flexShrink: 0 }}>
        <Box sx={{ bgcolor: DS.surface2, border: `1.5px solid ${DS.line}`, borderRadius: "12px",
          px: "14px", display: "flex", alignItems: "center", gap: "10px", height: 42,
          "&:focus-within": { borderColor: tPrimary, boxShadow: `0 0 0 3px ${tPrimary10}`, bgcolor: "#fff" },
          transition: "all .14s" }}>
          <Box component="svg" width={14} height={14} viewBox="0 0 24 24" fill="none"
            stroke={DS.ink3} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </Box>
          <Box component="input" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by title or course code…" autoFocus
            sx={{ flex: 1, border: 0, outline: 0, bgcolor: "transparent",
              fontSize: "13.5px", fontWeight: 500, color: DS.ink, fontFamily: "inherit",
              "&::placeholder": { color: DS.ink4, fontWeight: 400 } }} />
          {search && (
            <Box component="button" type="button" onClick={() => setSearch("")}
              sx={{ border: 0, bgcolor: "transparent", color: DS.ink4, cursor: "pointer",
                p: 0, display: "flex", "&:hover": { color: DS.ink } }}>
              <Box component="svg" width={13} height={13} viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Category filters */}
      {cats.length > 1 && (
        <Box sx={{ px: "20px", pt: "10px", display: "flex", gap: "6px", flexWrap: "wrap", flexShrink: 0 }}>
          {cats.map(cat => {
            const active = catFilter === cat || (cat === "All" && !catFilter);
            const cc = catColor(cat, tPrimary);
            return (
              <Box key={cat} component="button" type="button" onClick={() => setCatFilter(cat === "All" ? "" : cat)}
                sx={{ height: 28, px: "11px", borderRadius: "999px", border: "1.5px solid",
                  borderColor: active ? cc.color : DS.line, font: "inherit", fontSize: "12px",
                  fontWeight: 700, cursor: "pointer", transition: "all .12s",
                  bgcolor: active ? cc.bg : "#fff",
                  color: active ? cc.color : DS.ink3,
                  "&:hover": { borderColor: cc.color, color: cc.color, bgcolor: cc.bg } }}>
                {cat}
              </Box>
            );
          })}
        </Box>
      )}

      {/* List */}
      <Box sx={{ flex: 1, overflowY: "auto", px: "20px", py: "12px", minHeight: 0 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress size={28} sx={{ color: tPrimary }} />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 5, color: DS.ink4, fontSize: "13.5px", fontWeight: 500 }}>
            No courses found{search ? ` matching "${search}"` : ""}.
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            {filtered.map(cls => {
              const sel = isSelected(cls);
              const cc = catColor(cls.category, tPrimary);
              return (
                <Box key={cls.id} onClick={() => toggle(cls)}
                  sx={{ display: "flex", alignItems: "center", gap: "12px",
                    p: "11px 14px", borderRadius: "12px", cursor: "pointer",
                    border: `1.5px solid ${sel ? tPrimary : DS.line}`,
                    bgcolor: sel ? tPrimary10 : "#fff",
                    transition: "all .13s",
                    "&:hover": { borderColor: sel ? tPrimary : "var(--color-hairline-cool)", bgcolor: sel ? tPrimary10 : DS.surface2 } }}>
                  {/* Checkbox */}
                  <Box sx={{ width: 20, height: 20, borderRadius: "6px", flexShrink: 0,
                    border: `2px solid ${sel ? tPrimary : DS.line}`,
                    bgcolor: sel ? tPrimary : "#fff",
                    display: "grid", placeItems: "center", transition: "all .13s" }}>
                    {sel && (
                      <Box component="svg" width={11} height={11} viewBox="0 0 24 24" fill="none"
                        stroke="#fff" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </Box>
                    )}
                  </Box>
                  {/* Info */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                      <Typography sx={{ fontSize: "13.5px", fontWeight: 700,
                        color: sel ? tPrimary : DS.ink, lineHeight: 1.3 }}>
                        {cls.title}
                      </Typography>
                      {cls.category && (
                        <Box sx={{ fontSize: "10.5px", fontWeight: 800, px: "7px", py: "2px",
                          borderRadius: "999px", bgcolor: cc.bg, color: cc.color,
                          letterSpacing: ".04em" }}>
                          {cls.category}
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ display: "flex", gap: "10px", mt: "3px", flexWrap: "wrap" }}>
                      {cls.courseNumber && (
                        <Typography sx={{ fontSize: 12, color: DS.ink4, fontWeight: 600,
                          fontFamily: "'JetBrains Mono', monospace" }}>
                          #{cls.courseNumber}
                        </Typography>
                      )}
                      {cls.courseLevel && (
                        <Typography sx={{ fontSize: 12, color: DS.ink4, fontWeight: 500 }}>
                          {cls.courseLevel}
                        </Typography>
                      )}
                      {cls.totalHours && (
                        <Typography sx={{ fontSize: 12, color: DS.ink4, fontWeight: 500 }}>
                          {cls.totalHours} hrs
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between",
        px: "20px", py: "14px", borderTop: `1px solid ${DS.line}`, flexShrink: 0, bgcolor: DS.surface2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {draft.length > 0 ? (
            <>
              <Box sx={{ width: 22, height: 22, borderRadius: "6px", bgcolor: tPrimary,
                color: "#fff", fontSize: 11, fontWeight: 800, display: "grid", placeItems: "center" }}>
                {draft.length}
              </Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: DS.ink2 }}>
                {draft.length === 1 ? "course selected" : "courses selected"}
              </Typography>
              <Box component="button" type="button" onClick={() => setDraft([])}
                sx={{ border: 0, bgcolor: "transparent", fontSize: 12, fontWeight: 600,
                  color: DS.ink4, cursor: "pointer", fontFamily: "inherit", p: 0, ml: "4px",
                  "&:hover": { color: DS.danger } }}>
                Clear all
              </Box>
            </>
          ) : (
            <Typography sx={{ fontSize: 13, fontWeight: 500, color: DS.ink4 }}>No prerequisites selected</Typography>
          )}
        </Box>
        <Box component="button" type="button" onClick={() => { onApply(draft); onClose(); }}
          sx={{ height: 38, px: "18px", borderRadius: "10px", border: 0,
            bgcolor: tPrimary, color: "#fff", font: "inherit", fontSize: "13.5px",
            fontWeight: 700, cursor: "pointer", boxShadow: `0 4px 12px -4px ${alpha(tPrimary, 0.4)}`,
            transition: "all .13s", "&:hover": { bgcolor: tPrimary700 } }}>
          {draft.length > 0 ? `Add ${draft.length} Prerequisite${draft.length > 1 ? "s" : ""}` : "Done"}
        </Box>
      </Box>
    </Dialog>
  );
}

export default function CourseSubmissionPage() {
  const navigate = useNavigate();
  const { user } = useTenant();
  const CATEGORIES = useConfigList("COURSE_CATEGORY", DEFAULT_CATEGORIES);
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const tPrimary700 = theme.palette.primary.dark;
  const tPrimary10 = alpha(tPrimary, 0.06);
  const tPrimary50 = alpha(tPrimary, 0.10);
  const tSecondary = theme.palette.secondary.main;
  const tSecondary50 = alpha(tSecondary, 0.10);

  const [activeStep, setActiveStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateSearch, setTemplateSearch] = useState("");
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const [prereqPickerOpen, setPrereqPickerOpen] = useState(false);

  const openTemplatePicker = () => {
    setTemplatePickerOpen(true);
    setLoadingTemplates(true);
    setTemplateSearch("");
    fetchClassTemplates(user?.tenantId).then(d => { setTemplates(Array.isArray(d) ? d : []); setLoadingTemplates(false); }).catch(() => setLoadingTemplates(false));
  };

  const applyTemplate = (tpl) => {
    const objectives = (() => { try { const v = JSON.parse(tpl.objectives || "[]"); return Array.isArray(v) ? v.filter(Boolean) : []; } catch { return []; } })();
    setForm(prev => ({
      ...prev,
      title: tpl.title || prev.title,
      category: tpl.category || prev.category,
      totalHours: tpl.totalHours ? String(tpl.totalHours) : prev.totalHours,
      courseLevel: tpl.courseLevel || prev.courseLevel,
      targetAudience: tpl.targetAudience || prev.targetAudience,
      description: tpl.description || prev.description,
      objectives: objectives.length > 0 ? objectives : prev.objectives,
      prerequisiteIds: prev.prerequisiteIds,
      maxStudents: tpl.maxStudents ? String(tpl.maxStudents) : prev.maxStudents,
    }));
    setSelectedTemplate(tpl);
    setTemplatePickerOpen(false);
  };

  const [form, setForm] = useState({
    title: "",
    category: "",
    totalHours: "",
    courseLevel: "Intro",
    targetAudience: "",
    description: "",
    objectives: [""],
    prerequisiteIds: [],
    equipmentNeeded: "",
    maxStudents: "",
    location: "",
    justification: "",
  });

  const set = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const descRef = useRef(null);

  const formatDescription = (type) => {
    const el = descRef.current;
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const value = el.value;
    const before = value.slice(0, start);
    const selected = value.slice(start, end);
    const after = value.slice(end);

    let newValue = value;
    let newStart = start;
    let newEnd = end;

    switch (type) {
      case "bold":
        newValue = before + `**${selected || "bold text"}**` + after;
        newStart = start + 2;
        newEnd = selected ? end + 2 : start + 2 + "bold text".length;
        break;
      case "italic":
        newValue = before + `*${selected || "italic text"}*` + after;
        newStart = start + 1;
        newEnd = selected ? end + 1 : start + 1 + "italic text".length;
        break;
      case "list": {
        if (selected) {
          const lines = selected.split("\n");
          const allBulleted = lines.every((l) => l.startsWith("- "));
          const formatted = lines
            .map((l) => (allBulleted ? l.replace(/^- /, "") : `- ${l}`))
            .join("\n");
          newValue = before + formatted + after;
          newStart = start;
          newEnd = start + formatted.length;
        } else {
          newValue = before + "- " + after;
          newStart = start + 2;
          newEnd = start + 2;
        }
        break;
      }
      case "link":
        if (selected) {
          newValue = before + `[${selected}](https://)` + after;
          newStart = start + selected.length + 3;
          newEnd = start + selected.length + 3 + "https://".length;
        } else {
          newValue = before + `[link text](https://)` + after;
          newStart = start + 1;
          newEnd = start + 1 + "link text".length;
        }
        break;
      default:
        return;
    }

    setForm((prev) => ({ ...prev, description: newValue }));
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(newStart, newEnd);
    });
  };

  const wordCount = form.description.trim()
    ? form.description.trim().split(/\s+/).length
    : 0;
  const filledObjectives = form.objectives.filter((o) => o.trim());

  const addObjective = () =>
    setForm((p) => ({ ...p, objectives: [...p.objectives, ""] }));
  const removeObjective = (i) =>
    setForm((p) => ({
      ...p,
      objectives: p.objectives.length > 1
        ? p.objectives.filter((_, idx) => idx !== i)
        : p.objectives,
    }));
  const updateObjective = (i, val) =>
    setForm((p) => ({
      ...p,
      objectives: p.objectives.map((o, idx) => (idx === i ? val : o)),
    }));

  const progressItems = [
    {
      label: "Course title & category",
      done: !!(form.title.trim() && form.category),
    },
    {
      label: "Level & audience set",
      done: !!(form.courseLevel && form.targetAudience.trim()),
    },
    { label: "Description (min 50 words)", done: wordCount >= 50 },
    {
      label: "At least 1 learning objective",
      done: filledObjectives.length >= 1,
    },
    {
      label: "Max students set",
      done: !!(form.maxStudents && Number(form.maxStudents) > 0),
    },
    { label: "Justification added", done: !!form.justification.trim() },
  ];
  const completedCount = progressItems.filter((i) => i.done).length;
  const progressPct = Math.round((completedCount / progressItems.length) * 100);

  const validateStep = (step) => {
    const errs = {};
    if (step === 0) {
      if (!form.title.trim()) errs.title = "Required";
      if (!form.category) errs.category = "Required";
      if (!form.totalHours || Number(form.totalHours) <= 0)
        errs.totalHours = "Required";
      if (!form.targetAudience.trim()) errs.targetAudience = "Required";
      if (filledObjectives.length === 0)
        errs.objectives = "Add at least one objective";
    }
    if (step === 1) {
      if (!form.maxStudents || Number(form.maxStudents) <= 0)
        errs.maxStudents = "Required";
    }
    if (step === 2) {
      if (!form.justification.trim()) errs.justification = "Required";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleContinue = () => {
    if (validateStep(activeStep)) setActiveStep((p) => p + 1);
  };

  const handleBack = () => setActiveStep((p) => p - 1);

  const handleSubmit = async () => {
    if (!validateStep(2)) return;
    setSaving(true);
    try {
      await submitCourseForApproval({
        title: form.title,
        description: form.description,
        category: form.category,
        totalHours: Number(form.totalHours),
        prerequisites: form.prerequisiteIds.map(c => c.title).join(", "),
        prerequisiteClassIds: JSON.stringify(form.prerequisiteIds.map(c => c.id)),
        equipmentNeeded: form.equipmentNeeded,
        maxStudents: form.maxStudents ? Number(form.maxStudents) : null,
        objectives: filledObjectives.join("\n"),
        justification: form.justification,
        instructorId: user?.id,
        ...(selectedTemplate ? { bankCourseId: selectedTemplate.id } : {}),
      });
      setSubmitted(true);
    } catch {
      setErrors({ submit: "Failed to submit. Please try again." });
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setSubmitted(false);
    setActiveStep(0);
    setErrors({});
    setForm({
      title: "",
      category: "",
      totalHours: "",
      courseLevel: "Intro",
      targetAudience: "",
      description: "",
      objectives: [""],
      prerequisiteIds: [],
      equipmentNeeded: "",
      maxStudents: "",
      location: "",
      justification: "",
    });
  };

  // ── Success screen ──────────────────────────────────────────────────────────
  if (submitted) {
    return (
      <Container maxWidth="sm" sx={{ py: 10, textAlign: "center" }}>
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            bgcolor: tSecondary50,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 3,
          }}
        >
          <TaskAltIcon sx={{ fontSize: 40, color: tSecondary }} />
        </Box>
        <Typography
          sx={{ fontSize: 24, fontWeight: 800, color: DS.ink, mb: 1.5, fontFamily: "var(--font-display)" }}
        >
          Course Submitted for Review
        </Typography>
        <Typography sx={{ fontSize: 14, color: DS.ink3, mb: 5, lineHeight: 1.7 }}>
          Your proposal for <strong>"{form.title}"</strong> has been sent to the
          Course Coordinator. You'll be notified once a decision is made —
          typically within 3–5 business days.
        </Typography>
        <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
          <Button
            variant="outlined"
            onClick={() => navigate("/classes")}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "12px",
              borderColor: DS.line,
              color: DS.ink2,
              height: 44,
              px: 3,
              "&:hover": { borderColor: "var(--color-hairline-cool)", bgcolor: DS.surface3 },
            }}
          >
            Back to Classes
          </Button>
          <Button
            variant="contained"
            onClick={resetForm}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "12px",
              bgcolor: tPrimary,
              height: 44,
              px: 3,
              boxShadow: `0 4px 14px -4px ${alpha(tPrimary, 0.5)}`,
              "&:hover": { bgcolor: tPrimary700 },
            }}
          >
            Submit Another
          </Button>
        </Box>
      </Container>
    );
  }

  // ── Stepper component ───────────────────────────────────────────────────────
  const StepIndicator = () => (
    <Paper
      elevation={0}
      sx={{
        bgcolor: "#fff",
        border: `1px solid ${DS.line}`,
        borderRadius: "18px",
        boxShadow: DS.shadowSm,
        p: "22px 32px 20px",
        mb: "24px",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "center", position: "relative" }}>
        {STEPS.map((step, i) => {
          const isDone = i < activeStep;
          const isActive = i === activeStep;
          return (
            <Box
              key={step.title}
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "10px",
                position: "relative",
                zIndex: 1,
              }}
            >
              {/* Connector line */}
              {i < STEPS.length - 1 && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "18px",
                    left: "50%",
                    right: "-50%",
                    height: "2px",
                    bgcolor: isDone ? tSecondary : DS.line2,
                    zIndex: 0,
                    transition: "background .3s",
                  }}
                />
              )}
              {/* Circle */}
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  flexShrink: 0,
                  zIndex: 1,
                  display: "grid",
                  placeItems: "center",
                  fontSize: 14,
                  fontWeight: 800,
                  border: `2px solid ${isDone ? tSecondary : isActive ? tPrimary : DS.line}`,
                  bgcolor: isDone ? tSecondary : isActive ? tPrimary : "#fff",
                  color: isDone || isActive ? "#fff" : DS.ink3,
                  boxShadow: isActive ? `0 0 0 4px ${tPrimary10}` : "none",
                  transition: "all .25s",
                }}
              >
                {isDone ? (
                  <CheckIcon sx={{ fontSize: 16, strokeWidth: 3 }} />
                ) : (
                  i + 1
                )}
              </Box>
              {/* Label */}
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px", textAlign: "center" }}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: isActive ? tPrimary : isDone ? tSecondary : DS.ink3,
                    transition: "color .2s",
                    fontFamily: "var(--font-display)",
                  }}
                >
                  {step.title}
                </Typography>
                <Typography sx={{ fontSize: "11.5px", color: DS.ink4, fontWeight: 500 }}>
                  {step.sub}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Global progress bar */}
      <Box sx={{ mt: "16px" }}>
        {/* Progress bar removed — shown in sidebar only */}
      </Box>
    </Paper>
  );

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 3.5 } }}>
      <StepIndicator />

      {/* ── Content grid ── */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 292px" },
          gap: "20px",
          alignItems: "start",
        }}
      >
        {/* ═══════════ FORM PANEL ═══════════ */}
        <Paper
          elevation={0}
          sx={{
            bgcolor: "#fff",
            border: `1px solid ${DS.line}`,
            borderRadius: "18px",
            boxShadow: DS.shadowSm,
            overflow: "hidden",
          }}
        >
          {/* Form head */}
          <Box sx={{ p: "24px 28px 20px", borderBottom: `1px solid ${DS.line2}` }}>
            <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.03em", color: DS.ink, mb: "5px", fontFamily: "var(--font-display)" }}>
              {STEPS[activeStep].title}
            </Typography>
            <Typography sx={{ fontSize: 14, color: DS.ink3 }}>
              {[
                "Define the primary attributes of your vocational course offering.",
                "Specify prerequisites, materials, and enrollment logistics for this course.",
                "Review your proposal and add a justification before sending for approval.",
              ][activeStep]}
            </Typography>
          </Box>

          {/* Form body */}
          <Box sx={{ p: "24px 28px", display: "flex", flexDirection: "column", gap: "22px" }}>

            {/* ── STEP 1: Course Details ── */}
            {activeStep === 0 && (
              <>
                {/* Template picker banner */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: "12px 16px", bgcolor: tPrimary10, border: `1px solid ${alpha(tPrimary, 0.14)}`, borderRadius: "12px", gap: "12px", flexWrap: "wrap" }}>
                  <Box>
                    <Box sx={{ fontSize: "13.5px", fontWeight: 700, color: tPrimary }}>
                      {selectedTemplate ? `Template: ${selectedTemplate.title}` : "Start from a template"}
                    </Box>
                    <Box sx={{ fontSize: "12px", color: DS.ink3, mt: "1px" }}>
                      {selectedTemplate ? "Fields pre-filled from bank template. Edit freely." : "Pre-fill this form from a saved course template."}
                    </Box>
                  </Box>
                  <Button size="small" variant="outlined" onClick={openTemplatePicker}
                    sx={{ textTransform: "none", fontWeight: 700, fontSize: "12.5px", borderRadius: "8px", borderColor: tPrimary, color: tPrimary, whiteSpace: "nowrap", "&:hover": { bgcolor: tPrimary10 } }}>
                    {selectedTemplate ? "Change Template" : "Browse Templates"}
                  </Button>
                </Box>

                {/* Title + Category */}
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "16px" }}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "7px" }}>
                    <Typography component="label" sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: DS.ink3 }}>
                      Course Title <Box component="span" sx={{ color: DS.danger, ml: "2px" }}>*</Box>
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="e.g. Advanced Firefighting Techniques"
                      value={form.title}
                      onChange={set("title")}
                      error={!!errors.title}
                      helperText={errors.title}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: 44,
                          borderRadius: "12px",
                          bgcolor: "#fff",
                          border: `1.5px solid ${DS.line}`,
                          fontSize: 14,
                          fontWeight: 500,
                          color: DS.ink,
                          "& fieldset": { border: "none" },
                          "&:hover": { borderColor: "var(--color-hairline-cool)" },
                          "&.Mui-focused": {
                            borderColor: tPrimary,
                            boxShadow: `0 0 0 3px ${alpha(tPrimary, 0.1)}`,
                            bgcolor: "#fff",
                          },
                        },
                        "& .MuiInputBase-input::placeholder": { color: DS.ink4, fontWeight: 400 },
                      }}
                    />
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "7px" }}>
                    <Typography component="label" sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: DS.ink3 }}>
                      Department / Category <Box component="span" sx={{ color: DS.danger, ml: "2px" }}>*</Box>
                    </Typography>
                    <TextField
                      fullWidth
                      select
                      SelectProps={{ native: true }}
                      value={form.category}
                      onChange={set("category")}
                      error={!!errors.category}
                      helperText={errors.category}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: 44,
                          borderRadius: "12px",
                          bgcolor: "#fff",
                          border: `1.5px solid ${DS.line}`,
                          fontSize: 14,
                          fontWeight: 500,
                          color: DS.ink,
                          "& fieldset": { border: "none" },
                          "&:hover": { borderColor: "var(--color-hairline-cool)" },
                          "&.Mui-focused": {
                            borderColor: tPrimary,
                            boxShadow: `0 0 0 3px ${alpha(tPrimary, 0.1)}`,
                            bgcolor: "#fff",
                          },
                        },
                      }}
                    >
                      <option value="">Select a category</option>
                      {CATEGORIES.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </TextField>
                  </Box>
                </Box>

                {/* Hours + Audience */}
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "16px" }}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "7px" }}>
                    <Typography component="label" sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: DS.ink3 }}>
                      Class Time (hrs) <Box component="span" sx={{ color: DS.danger, ml: "2px" }}>*</Box>
                    </Typography>
                    <TextField
                      fullWidth
                      type="number"
                      value={form.totalHours}
                      onChange={set("totalHours")}
                      error={!!errors.totalHours}
                      helperText={errors.totalHours}
                      inputProps={{ min: 1 }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: 44,
                          borderRadius: "12px",
                          bgcolor: "#fff",
                          border: `1.5px solid ${DS.line}`,
                          fontSize: 14,
                          fontWeight: 500,
                          color: DS.ink,
                          "& fieldset": { border: "none" },
                          "&:hover": { borderColor: "var(--color-hairline-cool)" },
                          "&.Mui-focused": {
                            borderColor: tPrimary,
                            boxShadow: `0 0 0 3px ${alpha(tPrimary, 0.1)}`,
                            bgcolor: "#fff",
                          },
                        },
                      }}
                    />
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "7px" }}>
                    <Typography component="label" sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: DS.ink3 }}>
                      Target Audience <Box component="span" sx={{ color: DS.danger, ml: "2px" }}>*</Box>
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="e.g. Junior Firefighters"
                      value={form.targetAudience}
                      onChange={set("targetAudience")}
                      error={!!errors.targetAudience}
                      helperText={errors.targetAudience}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: 44,
                          borderRadius: "12px",
                          bgcolor: "#fff",
                          border: `1.5px solid ${DS.line}`,
                          fontSize: 14,
                          fontWeight: 500,
                          color: DS.ink,
                          "& fieldset": { border: "none" },
                          "&:hover": { borderColor: "var(--color-hairline-cool)" },
                          "&.Mui-focused": {
                            borderColor: tPrimary,
                            boxShadow: `0 0 0 3px ${alpha(tPrimary, 0.1)}`,
                            bgcolor: "#fff",
                          },
                        },
                        "& .MuiInputBase-input::placeholder": { color: DS.ink4, fontWeight: 400 },
                      }}
                    />
                  </Box>
                </Box>

                {/* Course Level */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: "7px" }}>
                  <Typography component="label" sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: DS.ink3 }}>
                    Course Level <Box component="span" sx={{ color: DS.danger, ml: "2px" }}>*</Box>
                  </Typography>
                  <Box sx={{ display: "flex", gap: 0, bgcolor: DS.surface3, border: `1.5px solid ${DS.line}`, borderRadius: "12px", p: "3px", overflow: "hidden" }}>
                    {LEVELS.map((lvl) => (
                      <Button
                        key={lvl.value}
                        onClick={() => setForm((p) => ({ ...p, courseLevel: lvl.value }))}
                        sx={{
                          flex: 1,
                          height: 38,
                          borderRadius: "9px",
                          bgcolor: form.courseLevel === lvl.value ? "#fff" : "transparent",
                          color: form.courseLevel === lvl.value ? tPrimary : DS.ink3,
                          fontSize: 13,
                          fontWeight: 700,
                          textTransform: "none",
                          boxShadow: form.courseLevel === lvl.value ? DS.shadowSm : "none",
                          "&:hover": {
                            bgcolor: form.courseLevel === lvl.value ? "#fff" : "transparent",
                            color: form.courseLevel === lvl.value ? tPrimary : DS.ink,
                          },
                        }}
                      >
                        {lvl.label}
                      </Button>
                    ))}
                  </Box>
                </Box>

                {/* Description */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: "7px" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography component="label" sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: DS.ink3 }}>
                      Course Description <Box component="span" sx={{ color: DS.danger, ml: "2px" }}>*</Box>
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: wordCount >= 50 ? tSecondary : DS.ink4, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                      {wordCount} / 50 min
                    </Typography>
                  </Box>
                  <Box sx={{ border: `1.5px solid ${DS.line}`, borderRadius: "12px", overflow: "hidden" }}>
                    <Box sx={{ display: "flex", gap: 0.5, px: 1, py: 0.75, bgcolor: "#fff", borderBottom: `1px solid ${DS.line}` }}>
                      {[
                        { Icon: FormatBoldIcon, action: "bold", title: "Bold" },
                        { Icon: FormatItalicIcon, action: "italic", title: "Italic" },
                        { Icon: FormatListBulletedIcon, action: "list", title: "Bulleted list" },
                        { Icon: LinkIcon, action: "link", title: "Link" },
                      ].map(({ Icon, action, title }) => (
                        <IconButton
                          key={action}
                          size="small"
                          title={title}
                          onClick={() => formatDescription(action)}
                          sx={{ color: DS.ink4, "&:hover": { color: tPrimary } }}
                        >
                          <Icon sx={{ fontSize: 17 }} />
                        </IconButton>
                      ))}
                    </Box>
                    <TextField
                      fullWidth
                      multiline
                      minRows={4}
                      value={form.description}
                      onChange={set("description")}
                      placeholder="Provide a comprehensive overview of the course scope and outcomes. Include key competencies students will develop and how this course fits within their training track."
                      variant="standard"
                      inputRef={descRef}
                      InputProps={{ disableUnderline: true }}
                      sx={{
                        "& .MuiInputBase-root": {
                          p: "13px 14px",
                          alignItems: "flex-start",
                          bgcolor: "#fff",
                          fontSize: 14,
                          "&.Mui-focused": { bgcolor: "#fff" },
                        },
                        "& .MuiInputBase-input": { lineHeight: 1.6 },
                        "& .MuiInputBase-input::placeholder": { color: DS.ink4 },
                      }}
                    />
                  </Box>
                </Box>

                {/* Learning Objectives */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: "7px" }}>
                  <Typography component="label" sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: DS.ink3 }}>
                    Learning Objectives <Box component="span" sx={{ color: DS.danger, ml: "2px" }}>*</Box>
                  </Typography>
                  {errors.objectives && (
                    <Typography sx={{ fontSize: "0.75rem", color: DS.danger, mb: 0.5 }}>
                      {errors.objectives}
                    </Typography>
                  )}
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {form.objectives.map((obj, i) => (
                      <Box
                        key={i}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          bgcolor: "#fff",
                          border: `1.5px solid ${DS.line}`,
                          borderRadius: "11px",
                          pl: "14px",
                          overflow: "hidden",
                        }}
                      >
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: DS.ink3, flexShrink: 0, minWidth: 22 }}>
                          {i + 1}.
                        </Typography>
                        <TextField
                          fullWidth
                          value={obj}
                          onChange={(e) => updateObjective(i, e.target.value)}
                          placeholder={`e.g. Demonstrate proper donning of SCBA equipment`}
                          variant="standard"
                          InputProps={{ disableUnderline: true }}
                          sx={{
                            "& .MuiInputBase-root": { height: 42, bgcolor: "transparent" },
                            "& .MuiInputBase-input": { fontSize: 14, color: DS.ink, p: 0 },
                            "& .MuiInputBase-input::placeholder": { color: DS.ink4 },
                          }}
                        />
                        <IconButton
                          onClick={() => removeObjective(i)}
                          sx={{
                            width: 42,
                            height: 42,
                            borderRadius: 0,
                            color: DS.ink4,
                            flexShrink: 0,
                            "&:hover": { color: DS.danger, bgcolor: DS.danger50 },
                          }}
                          title="Remove"
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      onClick={addObjective}
                      startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                      sx={{
                        alignSelf: "flex-start",
                        height: 38,
                        px: "16px",
                        borderRadius: "10px",
                        border: `1.5px dashed ${DS.line}`,
                        bgcolor: "transparent",
                        color: tPrimary,
                        fontSize: 13,
                        fontWeight: 600,
                        textTransform: "none",
                        "&:hover": { bgcolor: tPrimary10, borderColor: tPrimary50 },
                      }}
                    >
                      Add objective
                    </Button>
                  </Box>
                </Box>
              </>
            )}

            {/* ── STEP 2: Requirements & Logistics ── */}
            {activeStep === 1 && (
              <>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "7px" }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography component="label" sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: DS.ink3 }}>
                      Prerequisite Courses
                      <Box component="span" sx={{ fontWeight: 500, textTransform: "none", letterSpacing: 0, color: DS.ink4, ml: "5px", fontSize: "11px" }}>(optional)</Box>
                    </Typography>
                    {form.prerequisiteIds.length > 0 && (
                      <Box component="button" type="button" onClick={() => setPrereqPickerOpen(true)}
                        sx={{ border: 0, bgcolor: "transparent", fontSize: 12, fontWeight: 700,
                          color: tPrimary, cursor: "pointer", fontFamily: "inherit", p: 0,
                          "&:hover": { textDecoration: "underline" } }}>
                        Edit
                      </Box>
                    )}
                  </Box>

                  {/* Trigger / chips area */}
                  <Box onClick={() => setPrereqPickerOpen(true)}
                    sx={{ minHeight: 52, px: "14px", py: "10px", cursor: "pointer",
                      bgcolor: "#fff", border: `1.5px solid ${DS.line}`, borderRadius: "12px",
                      display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center",
                      transition: "border-color .14s, box-shadow .14s",
                      "&:hover": { borderColor: "var(--color-hairline-cool)" },
                    }}>
                    {form.prerequisiteIds.length === 0 ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: "9px", color: DS.ink4 }}>
                        <Box component="svg" width={15} height={15} viewBox="0 0 24 24" fill="none"
                          stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                        </Box>
                        <Typography sx={{ fontSize: "13.5px", fontWeight: 400, color: DS.ink4 }}>
                          Search and select required prerequisite courses…
                        </Typography>
                      </Box>
                    ) : (
                      form.prerequisiteIds.map(p => {
                        const cc = catColor(p.category, tPrimary);
                        return (
                          <Box key={p.id} sx={{ display: "inline-flex", alignItems: "center", gap: "6px",
                            height: 28, pl: "10px", pr: "6px", borderRadius: "8px",
                            border: `1.5px solid ${cc.color}20`, bgcolor: cc.bg, color: cc.color }}>
                            <Typography sx={{ fontSize: "12.5px", fontWeight: 700 }}>{p.title}</Typography>
                            <Box component="button" type="button"
                              onClick={e => { e.stopPropagation(); setForm(f => ({ ...f, prerequisiteIds: f.prerequisiteIds.filter(x => x.id !== p.id) })); }}
                              sx={{ width: 18, height: 18, borderRadius: "5px", border: 0,
                                bgcolor: "transparent", color: "inherit", cursor: "pointer",
                                display: "grid", placeItems: "center", opacity: .7,
                                "&:hover": { opacity: 1, bgcolor: `${cc.color}20` } }}>
                              <Box component="svg" width={10} height={10} viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                              </Box>
                            </Box>
                          </Box>
                        );
                      })
                    )}
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: "6px", fontSize: 12, color: DS.ink3, fontWeight: 500 }}>
                    <InfoIcon sx={{ fontSize: 12, color: DS.ink4, flexShrink: 0 }} />
                    Students must hold a completion certificate for each selected course to register
                  </Box>
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: "7px" }}>
                  <Typography component="label" sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: DS.ink3 }}>
                    Equipment / Materials Needed
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    value={form.equipmentNeeded}
                    onChange={set("equipmentNeeded")}
                    placeholder="e.g. SCBA gear, turnout gear, textbooks, safety goggles..."
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        bgcolor: "#fff",
                        border: `1.5px solid ${DS.line}`,
                        fontSize: 14,
                        fontWeight: 500,
                        color: DS.ink,
                        "& fieldset": { border: "none" },
                        "&:hover": { borderColor: "var(--color-hairline-cool)" },
                        "&.Mui-focused": {
                          borderColor: tPrimary,
                          boxShadow: `0 0 0 3px ${alpha(tPrimary, 0.1)}`,
                          bgcolor: "#fff",
                        },
                      },
                      "& .MuiInputBase-input": { lineHeight: 1.6, p: "13px 14px" },
                      "& .MuiInputBase-input::placeholder": { color: DS.ink4, fontWeight: 400 },
                    }}
                  />
                </Box>

                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "16px" }}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "7px" }}>
                    <Typography component="label" sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: DS.ink3 }}>
                      Maximum Students <Box component="span" sx={{ color: DS.danger, ml: "2px" }}>*</Box>
                    </Typography>
                    <TextField
                      fullWidth
                      type="number"
                      value={form.maxStudents}
                      onChange={set("maxStudents")}
                      error={!!errors.maxStudents}
                      helperText={errors.maxStudents}
                      inputProps={{ min: 1, max: 100 }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: 44,
                          borderRadius: "12px",
                          bgcolor: "#fff",
                          border: `1.5px solid ${DS.line}`,
                          fontSize: 14,
                          fontWeight: 500,
                          color: DS.ink,
                          "& fieldset": { border: "none" },
                          "&:hover": { borderColor: "var(--color-hairline-cool)" },
                          "&.Mui-focused": {
                            borderColor: tPrimary,
                            boxShadow: `0 0 0 3px ${alpha(tPrimary, 0.1)}`,
                            bgcolor: "#fff",
                          },
                        },
                      }}
                    />
                    <Box sx={{ display: "flex", alignItems: "center", gap: "6px", fontSize: 12, color: DS.ink3, fontWeight: 500 }}>
                      <InfoIcon sx={{ fontSize: 12, color: DS.ink4, flexShrink: 0 }} />
                      Recommended class size: 12–24
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "7px" }}>
                    <Typography component="label" sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: DS.ink3 }}>
                      Preferred Location
                    </Typography>
                    <TextField
                      fullWidth
                      select
                      SelectProps={{ native: true }}
                      value={form.location}
                      onChange={set("location")}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: 44,
                          borderRadius: "12px",
                          bgcolor: "#fff",
                          border: `1.5px solid ${DS.line}`,
                          fontSize: 14,
                          fontWeight: 500,
                          color: DS.ink,
                          "& fieldset": { border: "none" },
                          "&:hover": { borderColor: "var(--color-hairline-cool)" },
                          "&.Mui-focused": {
                            borderColor: tPrimary,
                            boxShadow: `0 0 0 3px ${alpha(tPrimary, 0.1)}`,
                            bgcolor: "#fff",
                          },
                        },
                      }}
                    >
                      <option value="">No preference</option>
                      <option>Main Training Campus</option>
                      <option>Room 101</option>
                      <option>Room 204</option>
                      <option>Outdoor Training Grounds</option>
                    </TextField>
                  </Box>
                </Box>
              </>
            )}

            {/* ── STEP 3: Review & Submit ── */}
            {activeStep === 2 && (
              <>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                  {/* Course Details review */}
                  <ReviewCard
                    title="Course Details"
                    icon={<SchoolIcon sx={{ fontSize: 14, color: tPrimary }} />}
                    onEdit={() => setActiveStep(0)}
                  >
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
                      <ReviewCell label="Course Title" value={form.title || "—"} />
                      <ReviewCell label="Category" value={form.category || "—"} />
                      <ReviewCell label="Class Time" value={form.totalHours ? `${form.totalHours} hrs` : "—"} />
                      <ReviewCell label="Level" value={LEVELS.find((l) => l.value === form.courseLevel)?.label || form.courseLevel} />
                      <ReviewCell label="Target Audience" value={form.targetAudience || "—"} />
                      <ReviewCell label="Description" value={form.description || "—"} />
                      <Box sx={{ p: "13px 18px", gridColumn: "1 / -1", borderBottom: 0 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: DS.ink3, mb: "4px" }}>
                          Learning Objectives
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                          {filledObjectives.length > 0 ? (
                            filledObjectives.map((o, i) => (
                              <Box key={i} sx={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "13.5px", color: DS.ink2, fontWeight: 500 }}>
                                <Typography component="span" sx={{ fontSize: 11, fontWeight: 700, color: DS.ink3, minWidth: 18 }}>
                                  {i + 1}.
                                </Typography>
                                {o}
                              </Box>
                            ))
                          ) : (
                            <Typography sx={{ color: DS.ink4, fontSize: 13 }}>No objectives added</Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </ReviewCard>

                  {/* Requirements review */}
                  <ReviewCard
                    title="Requirements & Logistics"
                    icon={<CheckCircleIcon sx={{ fontSize: 14, color: tPrimary }} />}
                    onEdit={() => setActiveStep(1)}
                  >
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
                      <Box sx={{ p: "13px 18px", borderBottom: `1px solid ${DS.line3}` }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: DS.ink3, mb: "6px" }}>Prerequisites</Typography>
                        {form.prerequisiteIds.length === 0 ? (
                          <Typography sx={{ fontSize: "13.5px", fontWeight: 500, color: DS.ink3 }}>None specified</Typography>
                        ) : (
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
                            {form.prerequisiteIds.map(p => {
                              const cc = catColor(p.category, tPrimary);
                              return (
                                <Box key={p.id} sx={{ fontSize: "12px", fontWeight: 700, px: "9px", py: "3px",
                                  borderRadius: "7px", bgcolor: cc.bg, color: cc.color,
                                  border: `1px solid ${cc.color}25` }}>
                                  {p.title}
                                </Box>
                              );
                            })}
                          </Box>
                        )}
                      </Box>
                      <ReviewCell label="Equipment / Materials" value={form.equipmentNeeded || "None specified"} />
                      <ReviewCell label="Max Students" value={form.maxStudents ? `${form.maxStudents} students` : "—"} />
                    </Box>
                  </ReviewCard>
                </Box>

                {/* Justification */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: "7px" }}>
                  <Typography component="label" sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: DS.ink3 }}>
                    Justification for Approval <Box component="span" sx={{ color: DS.danger, ml: "2px" }}>*</Box>
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    value={form.justification}
                    onChange={set("justification")}
                    error={!!errors.justification}
                    helperText={errors.justification}
                    placeholder="Explain why this course is needed and how it benefits the department. Include any community need, certification alignment, or gap in current offerings..."
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        bgcolor: "#fff",
                        border: `1.5px solid ${DS.line}`,
                        fontSize: 14,
                        fontWeight: 500,
                        color: DS.ink,
                        "& fieldset": { border: "none" },
                        "&:hover": { borderColor: "var(--color-hairline-cool)" },
                        "&.Mui-focused": {
                          borderColor: tPrimary,
                          boxShadow: `0 0 0 3px ${alpha(tPrimary, 0.1)}`,
                          bgcolor: "#fff",
                        },
                      },
                      "& .MuiInputBase-input": { lineHeight: 1.6, p: "13px 14px" },
                      "& .MuiInputBase-input::placeholder": { color: DS.ink4, fontWeight: 400 },
                    }}
                  />
                </Box>

                {errors.submit && (
                  <Typography sx={{ fontSize: "0.83rem", color: DS.danger }}>
                    {errors.submit}
                  </Typography>
                )}

                {/* Review notice */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "13px",
                    bgcolor: tPrimary10,
                    border: `1px solid ${alpha(tPrimary, 0.12)}`,
                    borderRadius: "12px",
                    p: "14px 16px",
                  }}
                >
                  <InfoIcon sx={{ fontSize: 18, color: tPrimary, flexShrink: 0, mt: "1px" }} />
                  <Typography sx={{ fontSize: 13, color: DS.ink2, lineHeight: 1.6, m: 0 }}>
                    Your proposal will be reviewed by the <strong>Course Coordinator</strong>. Approval typically takes <strong>3–5 business days</strong>. You'll receive an email notification once a decision is made.
                  </Typography>
                </Box>
              </>
            )}
          </Box>

          {/* Form foot */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: "16px 28px 20px",
              borderTop: `1px solid ${DS.line2}`,
              bgcolor: DS.surface2,
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Button
              onClick={activeStep === 0 ? () => navigate(-1) : handleBack}
              startIcon={activeStep === 0 ? undefined : <ArrowBackIcon sx={{ fontSize: 16 }} />}
              sx={{
                height: 44,
                px: "20px",
                borderRadius: "12px",
                fontSize: 14,
                fontWeight: 700,
                textTransform: "none",
                color: DS.ink3,
                border: `1.5px solid ${DS.line}`,
                bgcolor: "transparent",
                "&:hover": { bgcolor: DS.surface3, color: DS.ink },
              }}
            >
              {activeStep === 0 ? "Cancel" : "Back"}
            </Button>
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px", ml: "auto" }}>
              <Button
                startIcon={<SaveIcon sx={{ fontSize: 14 }} />}
                sx={{
                  height: 44,
                  px: "20px",
                  borderRadius: "12px",
                  fontSize: 14,
                  fontWeight: 700,
                  textTransform: "none",
                  color: DS.ink2,
                  border: `1.5px solid ${DS.line}`,
                  bgcolor: DS.surface2,
                  "&:hover": { bgcolor: DS.surface3, borderColor: "var(--color-hairline-cool)", color: DS.ink },
                }}
              >
                Save Draft
              </Button>
              {activeStep < STEPS.length - 1 ? (
                <Button
                  onClick={handleContinue}
                  endIcon={<ChevronRightIcon sx={{ fontSize: 16, transition: "transform .15s" }} />}
                  sx={{
                    height: 44,
                    px: "20px",
                    borderRadius: "12px",
                    fontSize: 14,
                    fontWeight: 700,
                    textTransform: "none",
                    bgcolor: tPrimary,
                    color: "#fff",
                    boxShadow: `0 4px 14px -4px ${alpha(tPrimary, 0.5)}`,
                    "&:hover": { bgcolor: tPrimary700, "& .MuiSvgIcon-root": { transform: "translateX(3px)" } },
                    "&:active": { transform: "translateY(1px)" },
                  }}
                >
                  {activeStep === 0 ? "Continue" : "Continue to Review"}
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={saving}
                  startIcon={<SendIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    height: 44,
                    px: "20px",
                    borderRadius: "12px",
                    fontSize: 14,
                    fontWeight: 700,
                    textTransform: "none",
                    bgcolor: tSecondary,
                    color: "#fff",
                    boxShadow: `0 4px 14px -4px ${alpha(tSecondary, 0.45)}`,
                    "&:hover": { bgcolor: "var(--color-success)" },
                    "&:active": { transform: "translateY(1px)" },
                  }}
                >
                  {saving ? "Submitting…" : "Submit for Approval"}
                </Button>
              )}
            </Box>
          </Box>
        </Paper>

        {/* ═══════════ SIDEBAR ═══════════ */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "14px", position: "sticky", top: 24 }}>
          {/* Progress card */}
          <Paper
            elevation={0}
            sx={{
              bgcolor: "#fff",
              border: `1px solid ${DS.line}`,
              borderRadius: "16px",
              boxShadow: DS.shadowSm,
              overflow: "hidden",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "9px", p: "14px 18px 12px", borderBottom: `1px solid ${DS.line2}` }}>
              <TaskAltIcon sx={{ fontSize: 16, color: tPrimary }} />
              <Typography sx={{ fontSize: "13.5px", fontWeight: 700, color: DS.ink, m: 0, fontFamily: "var(--font-display)" }}>
                Submission Progress
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: "12px 18px 8px", fontSize: "12.5px" }}>
              <Typography sx={{ fontWeight: 700, color: DS.ink, fontSize: "12.5px" }}>
                Step {activeStep + 1}: {STEPS[activeStep].title}
              </Typography>
              <Typography sx={{ fontWeight: 800, color: tPrimary, fontSize: "12.5px" }}>
                {progressPct}%
              </Typography>
            </Box>
            <Box sx={{ px: "18px", pb: "14px" }}>
              <Box sx={{ height: 6, bgcolor: DS.line3, borderRadius: "999px", overflow: "hidden" }}>
                <Box
                  sx={{
                    height: "100%",
                    bgcolor: tPrimary,
                    borderRadius: "999px",
                    transition: "width .4s cubic-bezier(.4,0,.2,1)",
                    width: `${progressPct}%`,
                  }}
                />
              </Box>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", borderTop: `1px solid ${DS.line2}` }}>
              {progressItems.map((item) => (
                <Box key={item.label} sx={{ p: "11px 18px", borderBottom: `1px solid ${DS.line2}`, "&:last-child": { borderBottom: 0 } }}>
                  <ProgressItem label={item.label} done={item.done} />
                </Box>
              ))}
            </Box>
          </Paper>

          {/* Tips card */}
          <Paper
            elevation={0}
            sx={{
              bgcolor: tPrimary,
              borderRadius: "16px",
              boxShadow: DS.shadowMd,
              p: "16px 18px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "9px", mb: "13px" }}>
              <Box sx={{ width: 30, height: 30, borderRadius: "8px", bgcolor: "rgba(255,255,255,.12)", color: "#fff", display: "grid", placeItems: "center", flexShrink: 0 }}>
                <InfoIcon sx={{ fontSize: 16 }} />
              </Box>
              <Typography sx={{ fontSize: "13.5px", fontWeight: 700, color: "#fff", m: 0, fontFamily: "var(--font-display)" }}>
                Submission Tips
              </Typography>
            </Box>
            {TIPS.map(({ Icon, text, strong, rest }, i) => (
              <Box
                key={i}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "10px",
                  py: "9px",
                  borderBottom: i < TIPS.length - 1 ? "1px solid rgba(255,255,255,.12)" : 0,
                }}
              >
                <Icon sx={{ fontSize: 15, color: "rgba(255,255,255,.65)", flexShrink: 0, mt: "1px" }} />
                <Typography sx={{ fontSize: "12.5px", color: "rgba(255,255,255,.75)", lineHeight: 1.55 }}>
                  {text}<strong style={{ color: "#fff", fontWeight: 700 }}>{strong}</strong>{rest}
                </Typography>
              </Box>
            ))}
          </Paper>
        </Box>
      </Box>

      {/* Prereq Picker Dialog */}
      <PrereqPickerDialog
        open={prereqPickerOpen}
        onClose={() => setPrereqPickerOpen(false)}
        selected={form.prerequisiteIds}
        onApply={ids => setForm(f => ({ ...f, prerequisiteIds: ids }))}
        tenantId={user?.tenantId}
      />

      {/* Template Picker Dialog */}
      <Dialog open={templatePickerOpen} onClose={() => setTemplatePickerOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "20px" } }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: "20px 24px 14px", borderBottom: `1px solid ${DS.line}` }}>
          <Box>
            <Box sx={{ fontSize: "17px", fontWeight: 800, color: DS.ink, letterSpacing: "-.02em", fontFamily: "var(--font-display)" }}>Course Templates</Box>
            <Box sx={{ fontSize: "12.5px", color: DS.ink3, mt: "2px" }}>Select a template to pre-fill this form</Box>
          </Box>
          <IconButton size="small" onClick={() => setTemplatePickerOpen(false)} sx={{ color: DS.ink3 }}>&times;</IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: "16px 24px", display: "flex", flexDirection: "column", gap: "12px" }}>
          <Box sx={{ bgcolor: DS.surface2, border: `1px solid ${DS.line}`, borderRadius: "12px", px: "14px", display: "flex", alignItems: "center", gap: "10px", height: 40 }}>
            <Box component="svg" width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={DS.ink3} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </Box>
            <Box component="input" value={templateSearch} onChange={e => setTemplateSearch(e.target.value)} placeholder="Search templates…"
              sx={{ flex: 1, border: 0, outline: 0, bgcolor: "transparent", fontSize: "13.5px", color: DS.ink }} />
          </Box>
          {loadingTemplates ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={28} sx={{ color: tPrimary }} /></Box>
          ) : templates.filter(t => !templateSearch || t.title?.toLowerCase().includes(templateSearch.toLowerCase())).length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4, color: DS.ink4, fontSize: "13.5px" }}>No templates found.</Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", maxHeight: 360, overflowY: "auto" }}>
              {templates.filter(t => !templateSearch || t.title?.toLowerCase().includes(templateSearch.toLowerCase())).map(tpl => (
                <Box key={tpl.id} onClick={() => applyTemplate(tpl)}
                  sx={{ p: "12px 14px", borderRadius: "12px", border: `1.5px solid ${DS.line}`, cursor: "pointer", "&:hover": { borderColor: tPrimary, bgcolor: tPrimary10 } }}>
                  <Box sx={{ fontSize: "14px", fontWeight: 700, color: DS.ink, mb: "2px" }}>{tpl.title}</Box>
                  {tpl.description && <Box sx={{ fontSize: "12.5px", color: DS.ink3, mb: "4px" }}>{tpl.description}</Box>}
                  <Box sx={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                    {tpl.category && <Box sx={{ fontSize: "11.5px", color: DS.ink4 }}>{tpl.category}</Box>}
                    {tpl.courseLevel && <Box sx={{ fontSize: "11.5px", color: DS.ink4 }}>· {tpl.courseLevel}</Box>}
                    {tpl.totalHours && <Box sx={{ fontSize: "11.5px", color: DS.ink4 }}>· {tpl.totalHours}h</Box>}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
}
